/**
 * RouteGuard.tsx — Proteção de rotas por role + allowedModules (ADR-003).
 *
 * Fail-closed: qualquer estado não autenticado/autorizado → redireciona para /entrar.
 * O guard nunca é a única barreira de segurança (ADR-003 §4); as Firestore rules
 * são a camada autoritativa. O guard é UX — bloqueia navegação antes do fetch.
 *
 * Hierarquia de acesso por role (ADR-003 §3):
 *   admin > financeiro > viewer
 *
 * Admin bypassa todos os guards de módulo.
 */

import { type ReactNode } from "react";
import { Navigate } from "react-router";
import { useAuth } from "./AuthContext";
import type { OrbModuleId, OrbRole } from "./auth.types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ROLE_RANK: Record<OrbRole, number> = {
  viewer: 0,
  financeiro: 1,
  admin: 2,
};

/** Verifica se o papel do usuário atende ao mínimo exigido pela rota */
function hasMinRole(userRole: OrbRole, minRole: OrbRole): boolean {
  return ROLE_RANK[userRole] >= ROLE_RANK[minRole];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RouteGuardProps {
  children: ReactNode;
  /**
   * Papel mínimo exigido para acessar a rota.
   * Se omitido, qualquer papel autenticado tem acesso ("viewer" como mínimo).
   */
  minRole?: OrbRole;
  /**
   * ID do módulo exigido em allowedModules do claim.
   * Admin bypassa este check — tem acesso a todos os módulos.
   */
  moduleId?: OrbModuleId;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

/**
 * Protege uma rota autenticada pelo Órbita.
 *
 * Fluxo:
 *   1. loading → renderiza null (tela de loading já tratada no AppShell)
 *   2. unauthenticated | no-access → redireciona para /entrar
 *   3. authenticated mas sem role suficiente → redireciona para /entrar
 *   4. authenticated com role suficiente → renderiza children
 */
export function RouteGuard({ children, minRole = "viewer", moduleId }: RouteGuardProps) {
  const { state } = useAuth();

  // Enquanto carrega o estado de auth, não renderiza nada
  // (o AppShell exibe tela de loading enquanto status === "loading")
  if (state.status === "loading") {
    return null;
  }

  // Sem autenticação ou sem claim do Órbita → login
  if (state.status === "unauthenticated" || state.status === "no-access") {
    return <Navigate to="/entrar" replace />;
  }

  const { user } = state;

  // Verifica papel mínimo
  if (!hasMinRole(user.claims.role, minRole)) {
    return <Navigate to="/entrar" replace />;
  }

  // Admin bypassa verificação de moduleId
  if (user.claims.role === "admin") {
    return <>{children}</>;
  }

  // Verifica se o módulo está habilitado para o usuário
  if (moduleId && !user.claims.allowedModules.includes(moduleId)) {
    return <Navigate to="/entrar" replace />;
  }

  return <>{children}</>;
}

// ---------------------------------------------------------------------------
// Guard de app autenticado (wrapper para a rota /app/*)
// ---------------------------------------------------------------------------

/**
 * Protege toda a área autenticada (/app/*).
 * Redireciona para /entrar se não autenticado ou sem claim orbita.
 */
export function AppGuard({ children }: { children: ReactNode }) {
  const { state } = useAuth();

  if (state.status === "loading") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--color-nav-bg)",
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          aria-label="Carregando"
          style={{ animation: "spin 1s linear infinite", color: "var(--color-nav-active)" }}
        >
          <circle
            cx="16"
            cy="16"
            r="12"
            stroke="currentColor"
            strokeWidth="2.5"
            fill="none"
            strokeDasharray="50"
            strokeDashoffset="25"
          />
        </svg>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (state.status === "unauthenticated" || state.status === "no-access") {
    return <Navigate to="/entrar" replace />;
  }

  return <>{children}</>;
}
